import React, { useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import ReactApexChart from 'react-apexcharts'
import ChartInteractions from './ChartInteractions'
import ChartLegend from './ChartLegend'
import useContainerWidth from '../../hooks/useContainerWidth'
import { useChartZoomSampling } from './hooks'
import {
  AXIS_LABEL_STYLE, AXIS_TITLE_STYLE, DS_COLORS, SINGLE_POINT_TOOLTIP_CLASSES, bucketShape,
  buildChartBaseOptions, singlePointOptions, storeGridCenter, withCategories
} from '../charts/config'

const CHART_ID = 'progression-controls-absolute'

const CHART_CLASSES = 'tw:[&_.apexcharts-toolbar]:hidden!'

const LEGEND_ITEMS = [
  { label: 'Contrôles terminés', lineClassName: 'tw:border-ds-info tw:border-solid' },
  { label: 'Contrôles déclenchant une alerte', lineClassName: 'tw:border-ds-danger tw:border-dashed' }
]

const BASE_OPTIONS = {
  ...buildChartBaseOptions('area', CHART_ID),
  colors: [DS_COLORS.info, DS_COLORS.danger],
  stroke: { curve: 'smooth', width: 2, dashArray: [0, 5] },
  fill: {
    type: ['gradient', 'solid'],
    gradient: { opacityFrom: 0.45, opacityTo: 0.1 },
    opacity: [1, 0]
  },
  dataLabels: { enabled: false },
  yaxis: {
    title: { text: 'Nombre de contrôles', style: AXIS_TITLE_STYLE, offsetX: -6 },
    labels: { formatter: value => Math.round(value), style: AXIS_LABEL_STYLE }
  }
}

const AbsoluteChart = ({ alerts, controls, hasAlertsConfigured }) => {
  const containerRef = useRef(null)
  const containerWidth = useContainerWidth(containerRef)
  const handleZoomed = useChartZoomSampling(controls, containerWidth)

  const options = useMemo(() => {
    const opts = withCategories(BASE_OPTIONS, controls, { containerWidth })
    return {
      ...opts,
      ...singlePointOptions(controls),
      chart: {
        ...opts.chart,
        events: {
          ...opts.chart.events,
          mounted: storeGridCenter,
          updated: storeGridCenter,
          zoomed: handleZoomed
        }
      }
    }
  }, [controls, containerWidth, handleZoomed])

  const series = useMemo(() => {
    const finished = {
      name: 'Contrôles terminés',
      data: controls.map(bucket => bucket.count)
    }
    if (!hasAlertsConfigured)
      return [finished]
    const alertsByPeriod = new Map(alerts.map(alertBucket => [alertBucket.period_start, alertBucket]))
    const alerted = {
      name: 'Contrôles déclenchant une alerte',
      data: controls.map(bucket => alertsByPeriod.get(bucket.period_start)?.count || 0)
    }
    return [finished, alerted]
  }, [alerts, controls, hasAlertsConfigured])

  return (
    <div
      ref={containerRef}
      className={`${CHART_CLASSES} ${controls.length === 1 ? SINGLE_POINT_TOOLTIP_CLASSES : ''}`.trim()}
    >
      <ReactApexChart options={options} series={series} type="area" height={320} />
      <ChartLegend
        actions={<ChartInteractions chartId={CHART_ID} />}
        items={hasAlertsConfigured ? LEGEND_ITEMS : []}
      />
    </div>
  )
}

AbsoluteChart.propTypes = {
  alerts: PropTypes.arrayOf(bucketShape).isRequired,
  controls: PropTypes.arrayOf(bucketShape).isRequired,
  hasAlertsConfigured: PropTypes.bool.isRequired
}

export default AbsoluteChart
